import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactUnityEventParameter } from "react-unity-webgl/distribution/types/react-unity-event-parameters";

import { unityViewerContext as nativeUnityContext } from "contexts/UnityViewerContext";

import { getHTTPUrlFromS3Path } from "utils/outputs";
import { vTourViewModes } from "utils/unityConstants";

import { useGetOutputQuery } from "api/output/queries/useGetOutputQuery";
import { useGetScanQuery } from "api/scan/queries/useGetScanQuery";
import { useCreateReferenceMeasurement } from "api/virtual-tour/mutations/useCreateReferenceMeasurementMutation";
import { useDeleteReferenceMeasurementMutation } from "api/virtual-tour/mutations/useDeleteReferenceMeasurementMutation";
import { useGetVirtualTourAnnotationsQuery } from "api/virtual-tour/queries/useGetVirtualTourAnnotationsQuery";
import { useGetVirtualTourReferenceMeasurements } from "api/virtual-tour/queries/useGetVirtualTourReferenceMeasurements";
import { currentVirtualViewerModeAtom } from "store/atoms/currentVirtualViewerModeAtom";
import { isAddCommentModeActiveAtom } from "store/atoms/isAddCommentModeActive";
import { isDiscardMeasurementModeActiveAtom } from "store/atoms/isDiscardMeasurementModeActive";
import { isMeasurementModeActiveAtom } from "store/atoms/isMeasurementModeActive";
import { isReferenceMeasurementModeActiveAtom } from "store/atoms/isReferenceMeasurementModeActive";
import { newAnnotationAtom } from "store/atoms/newAnnotation";
import { getPotreeCameraAtom } from "store/atoms/potree/getPotreeCamera";
import { newReferenceMeasurementValueAtom } from "store/atoms/newReferenceMeasurementValue";
import { referenceMeasurementIDToDeleteAtom } from "store/atoms/referenceMeasurementIDToDelete";
import { referenceMeasurementInputValueModalStateAtom } from "store/atoms/referenceMeasurementInputValueModalState";
import { vtourAnnotationsLengthAtom } from "store/atoms/vtourAnnotationsLengthAtom";
import { toggleReferenceMeasurementModeIsDisabledAtom } from "store/atoms/toggleReferenceMeasurementModeIsDisabled";
import { hasUnityLoadedOnceAtom } from "store/atoms/unityHasLoadedOnce";
import { useCurrentActiveScan } from "store/currentActiveScan";
import { currentHotspotEventDataAtom } from "store/atoms/CurrentHotspotEventDataAtom";
import { isTrimModeActiveAtom } from "store/atoms/virtualTourViewer/isTrimModeActive";
import { newCropBoxAtom } from "store/atoms/virtualTourViewer/newCropBoxAtom";
import { deleteCropBoxAtom } from "store/atoms/virtualTourViewer/deleteCropBoxAtom";
import { setToTranslateAtom } from "store/atoms/virtualTourViewer/setToTransformAtom";
import { setToRotateAtom } from "store/atoms/virtualTourViewer/setToRotate";
import { setToScaleAtom } from "store/atoms/virtualTourViewer/setToScale";
import { isPotreeFocuesAtom } from "store/atoms/potree/isPotreeFocusedAtom";
import { setPoreeCameraAtom } from "store/atoms/virtualTourViewer/setPotreeCameraAtom";
import { newAspectForCompareScanAtom } from "store/atoms/virtualTourViewer/newAspectForCompareScan";
import { useLoading } from "contexts/LoadingContext";
import { useGetScanTransformationQuery } from "api/scan/queries/useGetScanTransformationQuery";
import { isPathActiveAtom } from "store/atoms/isPathActiveAtom";
import { IDENTITY_MATRIX } from "utils/transformation";

type UnityEvent =
  | "OpenAnnotation"
  | "CreateAnnotation"
  | "CreateMeasurement"
  | "OnReferenceMeasurementEnded"
  | "CreateReferenceMeasurement"
  | "DeleteReferenceMeasurement"
  | "OnSelectMeasurement"
  | "OnSelectHotspot"
  | "OnGetCurrentHotspot"
  | "SendCameraLocation";
type UnityEventHandler = (...params: ReactUnityEventParameter[]) => void;

export type UnityViewType = "uniscan" | "biscan" | "uniscan-compare";

const useUnityViewer = (typeOfViewFromProps: UnityViewType = "uniscan") => {
  // * ===== INITIALIZATIONS =====

  const unityContext = useContext(nativeUnityContext);
  const [queryParams, setSearchParams] = useSearchParams();

  const typeOfView = queryParams.get("mode") || typeOfViewFromProps;

  const { vtourOutputId1, vtourOutputId2, scan1Data, scan2Data, scan1, scan2 } = useLoading();

  const [potreeCamera, setPotreeCamera] = useAtom(getPotreeCameraAtom);

  const hotspotId = queryParams.get("hotspotId");

  const unityCtxRef = useRef(unityContext);

  // * ===== STATES AND ATOMS =====

  // new annotation
  const setNewAnnotation = useSetAtom(newAnnotationAtom);

  // should annotations be refreshed
  const [vtourAnnotationsLength, setVtourAnnotationsLength] = useAtom(
    vtourAnnotationsLengthAtom
  );
  const resetShouldRefreshAnnotations = useResetAtom(
    vtourAnnotationsLengthAtom
  );

  // current vtour mode
  const [currentVTourMode, setCurrentVTourMode] = useAtom(
    currentVirtualViewerModeAtom
  );
  const resetCurrentVtourMode = useResetAtom(currentVirtualViewerModeAtom);

  const [newCropBox, setNewCropBox] = useAtom(newCropBoxAtom);
  const resetCropBox = useResetAtom(newCropBoxAtom);

  const [deleteCropBox, setDeleteCropBox] = useAtom(deleteCropBoxAtom);
  const resetDeleteCropBox = useResetAtom(deleteCropBoxAtom);

  const [isSetToTranslate, setToTranslate] = useAtom(setToTranslateAtom);
  const resetTranslate = useResetAtom(setToTranslateAtom);
  const [isPotreeCameraSetString, setPtreeCameraString] =
    useAtom(setPoreeCameraAtom);
  const [isSetToRotate, setToRotate] = useAtom(setToRotateAtom);
  const resetRotate = useResetAtom(setToRotateAtom);

  const [isSetToScale, setTotScale] = useAtom(setToScaleAtom);
  const resetScale = useResetAtom(setToScaleAtom);

  const [isPotreeFocus, setIsPotreeFocus] = useAtom(isPotreeFocuesAtom);
  const resetPotreeFocus = useResetAtom(isPotreeFocuesAtom);
  // is measurement mode active
  const [isMeasurementModeActive, setIsMeasurementModeActive] = useAtom(
    isMeasurementModeActiveAtom
  );

  const [newAspectForCompareScan, setNewAspectForCompareScan] = useAtom(
    newAspectForCompareScanAtom
  );

  const resetIsMeasurementModeActive = useResetAtom(
    isMeasurementModeActiveAtom
  );

  const [isTrimModeActive, setTrimMode] = useAtom(isTrimModeActiveAtom);

  const resetTrimMode = useResetAtom(isTrimModeActiveAtom);

  // is add comment mode active
  const [isAddCommentModeActive, setIsAddCommentModeActive] = useAtom(
    isAddCommentModeActiveAtom
  );
  const resetIsAddCommentModeActive = useResetAtom(isAddCommentModeActiveAtom);

  // is reference measurement mode active
  const [
    isReferenceMeasurementModeActive,
    setIsReferenceMeasurementModeActive,
  ] = useAtom(isReferenceMeasurementModeActiveAtom);
  const resetIsReferenceMeasurementModeActive = useResetAtom(
    isReferenceMeasurementModeActiveAtom
  );

  // is discard measurement mode active
  const isDiscardMeasurementModeActive = useAtomValue(
    isDiscardMeasurementModeActiveAtom
  );
  const resetIsDiscardMeasurementModeActive = useResetAtom(
    isDiscardMeasurementModeActiveAtom
  );

  const isPathActive=useAtomValue(isPathActiveAtom);

  const resetIsPathActive=useResetAtom(isPathActiveAtom);

  // toggle reference measurement mode is disabled
  const setToggleReferenceMeasurementModeIsDisabled = useSetAtom(
    toggleReferenceMeasurementModeIsDisabledAtom
  );
  const resetToggleReferenceMeasurementModeIsDisabled = useResetAtom(
    toggleReferenceMeasurementModeIsDisabledAtom
  );

  // enter reference measurement value input modal state
  const [
    referenceMeasurementInputValueModalState,
    setReferenceMeasurementInputValueModalState,
  ] = useAtom(referenceMeasurementInputValueModalStateAtom);
  const resetReferenceMeasurementInputValueModalState = useResetAtom(
    referenceMeasurementInputValueModalStateAtom
  );

  // new reference measurement value
  const newReferenceMeasurementValue = useAtomValue(
    newReferenceMeasurementValueAtom
  );

  // track if unity has loaded once so we don't refresh the ref
  const [hasUnityLoadedOnce, setHasUnityLoadedOnce] = useAtom(
    hasUnityLoadedOnceAtom
  );
  const resetHasUnityLoadedOnce = useResetAtom(hasUnityLoadedOnceAtom);

  const [referenceMeasurementIDToDelete, setReferenceMeasurementIDToDelete] =
    useAtom(referenceMeasurementIDToDeleteAtom);

  // for event data
  const [currentHotspotEventData, setCurrentHotspotEventData] = useAtom(
    currentHotspotEventDataAtom
  );

  const resetCurrentHotspotEventData = useResetAtom(
    currentHotspotEventDataAtom
  );

  // * ===== QUERIES =====
 // console.log("scan1Data", scan1Data);
 const { data: uniscanData } = useGetScanTransformationQuery(scan1 || "");

 const { data: scan1Transformation } = useGetScanTransformationQuery(scan1 || "");
 const { data: scan2Transformation } = useGetScanTransformationQuery(scan2 || "");

 const biscanData = {
   scan1: scan1Transformation,
   scan2: scan2Transformation
 };

  // current active scan
  const {
    currentActiveScan: { virtualTourOutput },
  } = useCurrentActiveScan(scan1);

  const cmpPtcldVtId = scan1Data?.outputs?.filter(
    (out) => out.type === "VIRTUALTOUR"
  )[0]?.id;

  // output query
  const { data: output } = useGetOutputQuery(
    vtourOutputId1 || (cmpPtcldVtId as string)
  );

  // annotations query
  const { data: annotations } = useGetVirtualTourAnnotationsQuery(
    output?.virtualTour?.id || ""
  );

  // reference measurements query
  const { data: referenceMeasurements } =
    useGetVirtualTourReferenceMeasurements(
      virtualTourOutput?.virtualTour?.id || ""
    );

  // * ===== MUTATIONS =====

  // create reference measurement mutation
  const {
    mutate: createReferenceMeasurement,
    isPending: measurementSavingIsPending,
  } = useCreateReferenceMeasurement();

  // delete reference measurement mutation
  const { mutate: deleteReferenceMeasurement } =
    useDeleteReferenceMeasurementMutation(output?.virtualTour?.id || "");

  // * ===== VARIABLES EG. EVENT HANDLERS =====

  const getCurrentHotspotId = useCallback(() => {
    if (!unityContext) return;
    setCurrentHotspotEventData("pending");
    unityContext?.sendMessage("Canvas", "GetCurrentHotspot");
  }, [unityContext]);

  // TODO: when the user closes the get current hostpot id dialog box, clear the currentHotspotEventData state

  const toggleMeasurementMode = useCallback(() => {
    if (!isMeasurementModeActive) {
      // if we are switching measurement mode on, then switch off comment mode
      setIsAddCommentModeActive(false);
    }
    setIsMeasurementModeActive(!isMeasurementModeActive);
  }, [
    isMeasurementModeActive,
    setIsAddCommentModeActive,
    setIsMeasurementModeActive,
  ]);

  const toggleCommentMode = useCallback(() => {
    if (!isAddCommentModeActive) {
      setIsMeasurementModeActive(false);
    }
    setIsAddCommentModeActive(!isAddCommentModeActive);
  }, [
    isAddCommentModeActive,
    setIsMeasurementModeActive,
    setIsAddCommentModeActive,
  ]);

  const disableCurrentActiveMode = useCallback(() => {
    setIsMeasurementModeActive(false);
    setIsAddCommentModeActive(false);
  }, [setIsMeasurementModeActive, setIsAddCommentModeActive]);

  const enterFullScreen = useCallback(() => {
    unityCtxRef.current?.requestFullscreen(true);
  }, [unityContext]);

  // this defines all keybindings
  // key -> keyboard symbol, value -> handler function
  const keyBindings = useMemo<Record<string, () => void>>(
    () => ({
      // m: () => toggleMeasurementMode(),
      // c: () => toggleCommentMode(),
      Escape: () => disableCurrentActiveMode(),
      //f: () => enterFullScreen(),
    }),
    [toggleMeasurementMode, toggleCommentMode, disableCurrentActiveMode, enterFullScreen]
  );
  
  useEffect(() => {
    const handleRightClick = (event: MouseEvent) => {
      event.preventDefault(); // Prevents the context menu
      disableCurrentActiveMode();
    };
  
    document.addEventListener("contextmenu", handleRightClick);
    return () => {
      document.removeEventListener("contextmenu", handleRightClick);
    };
  }, [disableCurrentActiveMode]);

  const eventHandlers = useMemo<Record<UnityEvent, UnityEventHandler>>(
    () => ({
      // open an annotation
      OpenAnnotation: (...params: ReactUnityEventParameter[]) => {
        console.log("We are here");
        const [newAnnotationID] = params;
        const annotationId = annotations?.find(
          ({ id, metadata }) =>
            id === newAnnotationID ||
            metadata?.initialIDInUnity === newAnnotationID
        )?.id;
        if (annotationId) {
          setSearchParams((prev) => ({
            ...Object.fromEntries(prev.entries()),
            annotationId,
          }));
        }
      },
      // create an annotation (comment )
      CreateAnnotation: (...params: ReactUnityEventParameter[]) => {
        console.log("We are creating a comment");
        const [newAnnotationFromUnity] = params;
        setNewAnnotation(JSON.parse(newAnnotationFromUnity as string));
      },
      // create a measurement
      CreateMeasurement: () => {
        // not saving measurements for now
        return;
      },
      CreateReferenceMeasurement: (
        ...params: Array<ReactUnityEventParameter>
      ) => {
        const [newReferenceMeasurement] = params;
        const referenceMeasurementObject = JSON.parse(
          newReferenceMeasurement?.toString() || ""
        ) as VirtualTourReferenceMeasurementUnityEventPayload;

        const payload = {
          positions: [
            referenceMeasurementObject.position1,
            referenceMeasurementObject.position2,
          ],
          tourId: output?.virtualTour?.id || "",
          distance: referenceMeasurementObject.value,
          metadata: {
            unityID: referenceMeasurementObject.id,
            scaleFactor: referenceMeasurementObject.scaleFactor,
          },
        };

        createReferenceMeasurement(payload, {
          onSuccess: () => {
            toast.success("Reference Annotation saved successfully");
          },
          onError: (err) => {
            toast.error("Failed to save reference annotation");
            // TODO: Setup a centralized logger to handle environment wise check
            if (process.env.NODE_ENV !== "production") console.error(err);
          },
        });
      },
      DeleteReferenceMeasurement: (
        ...params: Array<ReactUnityEventParameter>
      ) => {
        const [referenceMeasurementID] = params;
        // add it to be deleted next
        setReferenceMeasurementIDToDelete(referenceMeasurementID as string);
      },
      OnReferenceMeasurementEnded: () => {
        setReferenceMeasurementInputValueModalState("open");
      },
      OnSelectMeasurement: (...params: ReactUnityEventParameter[]) => {
        console.debug("measurement selected with params", params);
        console.debug(params[0] && JSON.parse(params[0].toString()));
      },
      OnSelectHotspot: (...params: ReactUnityEventParameter[]) => {
        console.debug("hotspot selected with params", params);
        console.debug(params[0] && JSON.parse(params[0].toString()));
        // set the current vtour view mode to virtual tour
        setCurrentVTourMode({
          name: "VirtualTour",
          calledFrom: "click-hotspot",
        });
      },
      OnGetCurrentHotspot: (...params: ReactUnityEventParameter[]) => {
        console.debug("current hotspot params: ", params);
        // set the current hotspot state
        setCurrentHotspotEventData(params[0]?.toString());
      },
      SendCameraLocation: (...params: ReactUnityEventParameter[]) => {
        //console.log("measurement selected with params", params);
        //console.log("this is the params",params[0] && JSON.parse(params[0].toString()));
        if (params[0]) setPtreeCameraString(params[0].toString());
      },
    }),

    [
      annotations,
      setNewAnnotation,
      setReferenceMeasurementInputValueModalState,
      createReferenceMeasurement,
      output?.virtualTour?.id,
      setSearchParams,
      setReferenceMeasurementIDToDelete,
      setCurrentVTourMode,
    ]
  );
  // * ===== EFFECTS =====
  let potreeImages: any = null;

  useEffect(() => {
    //console.log("This is it the apocoloypse");
    if (potreeImages == null) potreeImages = isPotreeFocus;
    //console.log("This is the potreeImage ")
    //  setInterval(intervalFunction
    //    , 5)
  }, [potreeCamera, isPotreeFocus]);
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded) {
      return;
    }
    //console.log("this are the potree ",potreeCamera," unity context ",unityContext," ");
    if (potreeCamera) {
      unityContext.sendMessage("Canvas", "SendLocationToReact");
    }
    //console.log("This is the potreeImage ")
    //  setInterval(intervalFunction
    //    , 5)
  }, [potreeCamera, unityContext]);
  // reset everything on mount
  useEffect(() => {
    resetHasUnityLoadedOnce();
    // if hotspotId is present in url, navigate to that hotspot rather than default one
    if (hotspotId && hotspotId !== "pending") {
      setCurrentVTourMode({
        name: "VirtualTour",
        calledFrom: "main-toolbar",
      });
    } else {
      resetCurrentVtourMode();
    }
    resetShouldRefreshAnnotations();
    resetIsAddCommentModeActive();
    resetIsDiscardMeasurementModeActive();
    resetIsMeasurementModeActive();
    resetIsReferenceMeasurementModeActive();
    resetReferenceMeasurementInputValueModalState();
    resetToggleReferenceMeasurementModeIsDisabled();
    resetTrimMode();
    resetCropBox();
    resetDeleteCropBox();
    resetTranslate();
    resetRotate();
    resetScale();
    resetIsPathActive();

    return () => {
      resetReferenceMeasurementInputValueModalState();
    };
  }, [
    resetCurrentVtourMode,
    resetHasUnityLoadedOnce,
    resetShouldRefreshAnnotations,
    resetIsAddCommentModeActive,
    resetIsDiscardMeasurementModeActive,
    resetIsMeasurementModeActive,
    resetIsReferenceMeasurementModeActive,
    resetReferenceMeasurementInputValueModalState,
    resetToggleReferenceMeasurementModeIsDisabled,
    resetTrimMode,
    resetCropBox,
    resetDeleteCropBox,
    resetTranslate,
    resetRotate,
    resetScale,
    resetIsPathActive
  ]);
  const addButton = () => {
    if (!unityContext || !unityContext.isLoaded || hasUnityLoadedOnce) {
      return;
    }
    unityContext.sendMessage("CropObjectManager", "CreateCropBox");
  };

  const cropBoxDeleteButton = () => {
    if (!unityContext || !unityContext.isLoaded || hasUnityLoadedOnce) {
      return;
    }
    unityContext.sendMessage("CropObjectManager", "ToggleCropBoxState");
  };
  const setTranslate = () => {
    if (!unityContext || !unityContext.isLoaded || hasUnityLoadedOnce) {
      return;
    }
    unityContext.sendMessage("Main Camera", "SetGizmosToTranslate");
  };

  const setRotate = () => {
    if (!unityContext || !unityContext.isLoaded || hasUnityLoadedOnce) {
      return;
    }
    unityContext.sendMessage("Main Camera", "SetGizmosToRotate");
  };

  const setScale = () => {
    if (!unityContext || !unityContext.isLoaded || hasUnityLoadedOnce) {
      return;
    }
    unityContext.sendMessage("Main Camera", "SetGizmosToScale");
  };
  const isEventistenerSet = useRef(false);
  useEffect(() => {
    setPtreeCameraString(null);
    if (isEventistenerSet.current == false) {
      interface MyEventTypeDetail extends Event {
        detail: string;
      }

      const handleMyEventType = (evt: MyEventTypeDetail) => {
        console.log("these are the details ", evt, " ", typeof evt.detail);
        if (unityCtxRef.current && unityCtxRef.current.isLoaded) {
          console.log("We are calling a hotspot");
          unityCtxRef.current.sendMessage(
            "Canvas",
            "OpenHotspot",
            evt.detail.toString()
          );
        }
      };

      window.addEventListener(
        "piHotSpotClickMiniMapEvent",
        handleMyEventType as EventListener,
        false
      );
      isEventistenerSet.current = true;
    }

    return () => {
      unityCtxRef.current = undefined;
      unityContext?.unload();
      window.removeEventListener(
        "piHotSpotClickMiniMapEvent",
        function (evt) {}
      );
      setPotreeCamera(null);
    };
  }, []);
  // update the context ref when unity context is loaded
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded || hasUnityLoadedOnce) {
      return;
    }
    //setHasUnityLoadedOnce(true);

    if (!unityCtxRef.current || !unityCtxRef.current.isLoaded) {
      unityCtxRef.current = unityContext;
    }

    setNewCropBox(() => addButton);
    setDeleteCropBox(() => cropBoxDeleteButton);
    setToTranslate(() => setTranslate);
    setToRotate(() => setRotate);
    setTotScale(() => setScale);
  }, [unityContext, setHasUnityLoadedOnce, hasUnityLoadedOnce]);

  const intervalFunction = () => {
    if (!unityContext || !unityContext.isLoaded) {
      return;
    }
    if (potreeImages == null) return;
    //console.dir(potreeImages);
    //console.log("We are inside the interval");
    if (potreeCamera && potreeCamera.position) {
      const potreeJson = {
        position:
          potreeImages?.focusedImage != null
            ? {
                x: potreeImages.sphere.position.x,
                y: potreeImages.sphere.position.y,
                z: potreeImages.sphere.position.z,
              }
            : {
                x: potreeCamera.position.x,
                y: potreeCamera.position.y,
                z: potreeCamera.position.z,
              },
        rotation: {
          x: potreeCamera.rotation.x,
          y: potreeCamera.rotation.y,
          z: potreeCamera.rotation.z,
        },
        fov: potreeCamera.fov,
        isFoucesed: potreeImages?.focusedImage != null,
      };
      const ptreeJson = JSON.stringify(potreeJson);
      //console.log("We are here for you "+ptreeJson);
      //console.dir(potreeCamera);
      unityContext.sendMessage("Canvas", "GetCameraDataPotree", ptreeJson);
    }
  };
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded) {
      return;
    }

    //console.log("newAspectForCompareScan", newAspectForCompareScan);
    unityContext.sendMessage(
      "Canvas",
      "ChangeWindowSize",
      newAspectForCompareScan
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAspectForCompareScan]);
  // send transforms path to Unity
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded || !output?.s3Path) {
      return;
    }
    //console.log("this is the output",output);
    const outputUrl = getHTTPUrlFromS3Path(output.s3Path);
    const transformsUrl = `${outputUrl}vtour/transforms_sparse_octree_textured.json`;
    //const transformsUrl="http://localhost:8001/transforms_sparse_octree_textured.json"
    unityContext.sendMessage(
      "JavascriptInteractor",
      "SetTransformsPathJavascriptInterface",
      transformsUrl
    );
    // unityContext.sendMessage(
    //       "Canvas",
    //       "SendLocationToReact",
    //     );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unityContext]);

  const sendSampleData = false;

  // for uniscan
  useEffect(() => {
    if (
      !unityContext ||
      !unityContext.isLoaded||typeOfView === "biscan" ||(!sendSampleData && !uniscanData?.scanWrtWorld)
      || queryParams.get('secondType') === 'aps' // disable in aps comparison as we are okay with unidirectional for now
    ) {
      return;
    }

    const sampleMatrix = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ];

    const transformsData = {
      data: {
        proj4String: "+proj=utm +datum=WGS84 +no_defs",
        mainScanURL: "",
        relativeScanURL: "",
        transformDataType: "uniscan", // can be uniscan or multiscan (just cool names)
        mainScanMatrix: sendSampleData
          ? sampleMatrix
          : uniscanData?.scanWrtWorld || IDENTITY_MATRIX,
        relativeScanMatrix: sampleMatrix
      },
    };

    console.log("Sending UNISCAN transforms data to Unity:", transformsData, typeOfView, biscanData);

    unityContext.sendMessage(
      "TransformManager",
      "ParseRelativeScan",
      JSON.stringify(transformsData)
    );
  }, [unityContext, typeOfView, uniscanData, sendSampleData]);

  // for biscan
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded || typeOfView !== "biscan") {
      return;
    }

    if (
      !sendSampleData &&
      (!scan1Data?.outputs ||
        !scan2Data?.outputs ||
        !biscanData?.scan1?.scanWrtWorld ||
        !biscanData?.scan2?.scanWrtWorld)
    ) {
      return;
    }

    const virtualTourOutputOfScan1 = scan1Data?.outputs?.find(
      (outputOfScan) => outputOfScan.type === "VIRTUALTOUR"
    );
    const virtualTourOutputOfScan2 = scan2Data?.outputs?.find(
      (outputOfScan) => outputOfScan.type === "VIRTUALTOUR"
    );

    if (
      !virtualTourOutputOfScan1?.s3Path ||
      !virtualTourOutputOfScan2?.s3Path
    ) {
      return;
    }

    const sampleMainMatrix = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ];

    const sampleRelativeMatrix = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ];

    const transformsData = {
      data: {
        proj4String: "+proj=utm +datum=WGS84 +no_defs",
        mainScanURL: `${getHTTPUrlFromS3Path(
          virtualTourOutputOfScan1.s3Path
        )}vtour/transforms_sparse_octree_textured.json`,
        relativeScanURL: `${getHTTPUrlFromS3Path(
          virtualTourOutputOfScan2.s3Path
        )}vtour/transforms_sparse_octree_textured.json`,
        transformDataType: "biscan", // can be uniscan or multiscan (just cool names)
        mainScanMatrix: sendSampleData
          ? sampleMainMatrix
          : biscanData?.scan1?.scanWrtWorld || IDENTITY_MATRIX,
        relativeScanMatrix: sendSampleData
          ? sampleRelativeMatrix
          : biscanData?.scan2?.scanWrtWorld || IDENTITY_MATRIX,
      },
    };

    console.log("Sending BISCAN transforms data to Unity:", transformsData);

    unityContext.sendMessage(
      "TransformManager",
      "ParseRelativeScan",
      JSON.stringify(transformsData)
    );
    setHasUnityLoadedOnce(true);
  }, [
    unityContext,
    typeOfView,
    scan1Data?.outputs,
    scan2Data?.outputs,
    biscanData?.scan1?.scanWrtWorld,
    biscanData?.scan2?.scanWrtWorld,
    sendSampleData,
  ]);

  // open the current hotspot
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded || !hotspotId) return;
    console.debug(`sending hotspotId as ${hotspotId} to Canvas::OpenHotspot`);

    unityContext.sendMessage("Canvas", "OpenHotspot", hotspotId);
  }, [unityContext, hotspotId]);

  // TODO: REMOVE SAMPLE DATA FUNCTIONALITY. IT INCREASES COMPLEXITY MEANINGLESSLY. IT'S PRESENT NOW JUST FOR EASE OF TESTING

  // send annotations to Unity
  const currentComments = useRef(0);
  useEffect(() => {
    if (
      !unityContext ||
      !unityContext.isLoaded ||
      currentComments.current === annotations?.length
    ) {
      return;
    }

    // On every output update, we need to give the new annotations to Unity
    const { comments } = annotations
      ? annotations.reduce(
          (prev, current) => {
            if (current.type === "TEXT") {
              return {
                ...prev,
                comments: [...prev.comments, current],
              };
            }

            return prev;
          },
          {
            comments: [] as VirtualTourAnnotation[],
            // measurements: [] as UnityVirtualTourMeasurement[],
          }
        )
      : { comments: [] };
    currentComments.current = annotations?.length ?? 0;
    unityContext.sendMessage(
      "JavascriptInteractor",
      "SetAnnotations",
      JSON.stringify({
        comments,
      })
    );

    // unityContextValues.sendMessage(
    //   "JavascriptInteractor",
    //   "SetMeasurements",
    //   JSON.stringify({ measurements })
    // );
  }, [
    annotations,
    unityContext,
    vtourAnnotationsLength,
    setVtourAnnotationsLength,
  ]);

  // send existing reference measurements to Unity
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded) {
      return;
    }
    if(referenceMeasurements && referenceMeasurements.length > 0) {
    unityContext.sendMessage(
      "Canvas",
      "DisableInstruction",
      referenceMeasurements && referenceMeasurements.length
        ? JSON.stringify(
            referenceMeasurements?.map((measurement) => ({
              id: measurement.metadata?.unityID || "",
              position1: measurement.positions[0],
              position2: measurement.positions[1],
              value: measurement.distance,
              scaleFactor: measurement.metadata?.scaleFactor,
            }))
          )
        : ""
    );}

    if(scan1Data?.calibrated){
      console.log("this is the scan1Data",scan1Data.calibrated);
      unityContext.sendMessage(
        "Canvas",
        "isCalibrated"
      );
    }
  }, [referenceMeasurements, unityContext, virtualTourOutput]);

useEffect(() => {

},[])

  // attach and unmount event handlers (web)
  useEffect(() => {
    // handle all key bindings

    const handleKeypress = (e: KeyboardEvent) => {
      if (!keyBindings[e.key]) return;
      const handlerFn = keyBindings[e.key];
      handlerFn();
    };

    document.addEventListener("keyup", handleKeypress);

    return () => document.removeEventListener("keyup", handleKeypress);
  }, [keyBindings]);

  // attach and unmount event listeners (unity)
  useEffect(() => {
    if (!unityContext || !unityContext.isLoaded) {
      return;
    }

    unityContext.addEventListener(
      "OpenAnnotation",
      eventHandlers.OpenAnnotation
    );
    unityContext.addEventListener(
      "CreateAnnotation",
      eventHandlers.CreateAnnotation
    );
    unityContext.addEventListener(
      "CreateMeasurement",
      eventHandlers.CreateMeasurement
    );
    unityContext.addEventListener(
      "SetReferencePoints",
      eventHandlers.OnReferenceMeasurementEnded
    );
    unityContext.addEventListener(
      "CreateReferenceMeasurement",
      eventHandlers.CreateReferenceMeasurement
    );
    unityContext.addEventListener(
      "DeleteReferenceMeasurement",
      eventHandlers.DeleteReferenceMeasurement
    );
    unityContext.addEventListener(
      "StoreMeasurement",
      eventHandlers.OnSelectMeasurement
    );
    unityContext.addEventListener(
      "HotSpotClicked",
      eventHandlers.OnSelectHotspot
    );
    unityContext.addEventListener(
      "CurrentHotSpot",
      eventHandlers.OnGetCurrentHotspot
    );
    unityContext.addEventListener(
      "SendCameraLocation",
      eventHandlers.SendCameraLocation
    );
    return () => {
      unityContext.removeEventListener(
        "OpenAnnotation",
        eventHandlers.OpenAnnotation
      );
      unityContext.removeEventListener(
        "CreateAnnotation",
        eventHandlers.CreateAnnotation
      );
      unityContext.removeEventListener(
        "CreateMeasurement",
        eventHandlers.CreateMeasurement
      );
      unityContext.removeEventListener(
        "SetReferencePoints",
        eventHandlers.OnReferenceMeasurementEnded
      );
      unityContext.removeEventListener(
        "CreateReferenceMeasurement",
        eventHandlers.CreateReferenceMeasurement
      );
      unityContext.removeEventListener(
        "DeleteReferenceMeasurement",
        eventHandlers.DeleteReferenceMeasurement
      );
      unityContext.removeEventListener(
        "StoreMeasurement",
        eventHandlers.OnSelectMeasurement
      );
      unityContext.removeEventListener(
        "HotSpotClicked",
        eventHandlers.OnSelectHotspot
      );
      unityContext.removeEventListener(
        "CurrentHotSpot",
        eventHandlers.OnGetCurrentHotspot
      ),
        unityContext.removeEventListener(
          "SendCameraLocation",
          eventHandlers.OnGetCurrentHotspot
        );
    };
  }, [unityContext, eventHandlers]);

  // delete any reference measurement queued for deletion
  useEffect(() => {
    /*
        ? if a measurement is being saved or there's no reference measurement ID to delete, return
        ? this is to prevent a delete event from being triggered for a measurement even before it's saved
      */
    if (measurementSavingIsPending || !referenceMeasurementIDToDelete) return;

    // delete the reference measurement
    deleteReferenceMeasurement(
      {
        unityID: referenceMeasurementIDToDelete,
      },
      {
        onSuccess: () => {
          toast.success("Reference Measurement deleted successfully");
          setReferenceMeasurementIDToDelete((prev) =>
            /*
                ? If the previous reference measurement ID to delete is not the same as the one that was just deleted,
                ? which means a new reference measurement was added for deletion while the previous one was being deleted,
                ? keep the new one so that that's deleted next
                ? otherwise set it to null
              */
            prev === referenceMeasurementIDToDelete ? null : prev
          );
        },
        onError: () => {
          console.error("Failed to delete reference measurement");
        },
      }
    );
  }, [
    deleteReferenceMeasurement,
    measurementSavingIsPending,
    referenceMeasurementIDToDelete,
    setReferenceMeasurementIDToDelete,
  ]);

  // change view mode
  useEffect(() => {
    if (!unityCtxRef.current || !unityCtxRef.current.isLoaded) {
      return;
    }

    if (
      currentVTourMode.name === "VirtualTour" &&
      currentVTourMode.calledFrom === "click-hotspot"
    ) {
      return;
    }

    const currentModeMetadata = vTourViewModes.find(
      ({ name }) => name === currentVTourMode.name
    );

    if (!currentModeMetadata) {
      return;
    }

    unityCtxRef.current.sendMessage(
      currentModeMetadata.gameObject,
      currentModeMetadata.method
    );
  }, [currentVTourMode]);

  // toggle measurement mode
  useEffect(() => {
    if (!unityCtxRef.current || !unityCtxRef.current.isLoaded) {
      return;
    }
    if (isMeasurementModeActive) {
      unityCtxRef.current.sendMessage("Ruler", "ActiveMeasurementState");
    } else {
      unityCtxRef.current.sendMessage("Ruler", "DeactivateMeasurementState");
    }
  }, [isMeasurementModeActive]);

  // handle measurement mode to be reference measurement mode or normal mode
  useEffect(() => {
    if (
      !unityCtxRef.current ||
      !unityCtxRef.current.isLoaded ||
      !isMeasurementModeActive
    ) {
      setIsReferenceMeasurementModeActive(null);
      return;
    }

    // whenever measurement mode is toggled
    if (referenceMeasurements?.length) {
      setIsReferenceMeasurementModeActive(false);
      setToggleReferenceMeasurementModeIsDisabled(false);
    } else {
      setIsReferenceMeasurementModeActive(true);
      setToggleReferenceMeasurementModeIsDisabled(true);
    }

    if(scan1Data?.calibrated){
      setIsReferenceMeasurementModeActive(false);
      setToggleReferenceMeasurementModeIsDisabled(false);
    }
  }, [
    isMeasurementModeActive,
    setIsReferenceMeasurementModeActive,
    referenceMeasurements,
    setToggleReferenceMeasurementModeIsDisabled,
  ]);

  // handle reference measurement mode change
  useEffect(() => {
    if (
      !unityCtxRef.current ||
      !unityCtxRef.current.isLoaded ||
      !isMeasurementModeActive ||
      isReferenceMeasurementModeActive === null
    ) {
      return;
    }

    if (isReferenceMeasurementModeActive) {
      unityCtxRef.current.sendMessage("Caliper", "SetCalibrateTrue");
    } else {
      unityCtxRef.current.sendMessage("Caliper", "SetCalibrateFalse");
    }
  }, [isMeasurementModeActive, isReferenceMeasurementModeActive]);

  // handle discard measurement mode change
  useEffect(() => {
    if (!unityCtxRef.current || !unityCtxRef.current.isLoaded) {
      return;
    }

    if (isDiscardMeasurementModeActive) {
      unityCtxRef.current.sendMessage("Delete", "EnableDeleting");
    } else {
      unityCtxRef.current.sendMessage("Delete", "DisableDeleting");
    }
  }, [isDiscardMeasurementModeActive]);

  useEffect(() => {
    if (!unityCtxRef.current || !unityCtxRef.current.isLoaded) {
      return;
    }
      // Convert boolean to number (0 or 1)
      console.log("is PathActive ",isPathActive);
      const pathActiveAsNumber = isPathActive ? 1 : 0;
      unityCtxRef.current.sendMessage("Canvas", "TooglePath", pathActiveAsNumber);
  }, [isPathActive]);

  // handle reference measurement input value modal state change
  useEffect(() => {
    if (!unityCtxRef.current || !unityCtxRef.current.isLoaded) {
      return;
    }

    if (referenceMeasurementInputValueModalState === "closed") {
      return;
    } else if (referenceMeasurementInputValueModalState === "cancelled") {
      unityCtxRef.current.sendMessage("Ruler", "onCancel");
      setReferenceMeasurementInputValueModalState("closed");
    } else if (
      referenceMeasurementInputValueModalState === "confirmed" &&
      newReferenceMeasurementValue !== null
    ) {
      unityCtxRef.current.sendMessage(
        "Ruler",
        "onConfirm",
        newReferenceMeasurementValue
      );
      setReferenceMeasurementInputValueModalState("closed");
    }
  }, [
    setReferenceMeasurementInputValueModalState,
    newReferenceMeasurementValue,
    referenceMeasurementInputValueModalState,
  ]);

  // toggle add comment mode
  useEffect(() => {
    if (!unityCtxRef.current || !unityCtxRef.current.isLoaded) {
      return;
    }

    if (isAddCommentModeActive) {
      unityCtxRef.current.sendMessage("CommentsLoader", "EnableCommentState");
    } else {
      unityCtxRef.current.sendMessage("CommentsLoader", "DisableCommentState");
    }
  }, [isAddCommentModeActive]);

  return {
    unityViewerContext: unityContext,
    actions: {
      getCurrentHotspotId,
    },
  };
};

export default useUnityViewer;
